import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "react-dates/initialize";
// import { DateRangePicker } from 'react-dates'
import DateRangePicker from "react-dates/lib/components/DateRangePicker";
import classes from "./styles.module.scss";
import {
  ANCHOR_LEFT,
  OPEN_DOWN,
  START_DATE,
  END_DATE,
} from "react-dates/constants";
import { datesAreSame } from "venuescanner-js/utils";
import moment from "moment";
import addMobileDatePickerTitle from "./addMobileDatePickerTitle";

import useFixPosition, { fixDatePickerLocation } from "./useFixPosition";
import useFixMobileClose from "./useFixMobileClose";
import useUpdateBodyClass from "./useUpdateBodyClass";

const VsDateRangePicker = ({
  value,
  onChange,
  direction = OPEN_DOWN,
  fixPosition = false,
  withPortal = false,
  allowPast = false,
  minDate = null,
  excludedDates = false,
  excludedDays = false,
}) => {
  const fullScreen = window.matchMedia("(max-width: 480px)").matches;
  const [focusedInput, setFocusedInput] = useState(null);
  const [touched, setTouched] = useState(false);

  useFixPosition(fixPosition, "DateRangePicker");
  useFixMobileClose(setFocusedInput);
  useUpdateBodyClass(touched);
  useEffect(() => {
    addMobileDatePickerTitle(focusedInput);
  }, [focusedInput]);

  let temporaryEndDate = value.end;
  minDate = minDate || (!allowPast ? moment() : null);
  const notWithinRange = (day) => {
    if (minDate && day.isBefore(minDate)) return true;
    if (!allowPast) return day.isBefore(moment());

    return false;
  };
  if (value.start && notWithinRange(value.start)) {
    onChange({ start: null, end: null, singleDay: false });
  }

  if (!minDate) minDate = moment().subtract(6, "month");

  const renderCalendarFooter = () => {
    return (
      <div className="date-picker-footer">
        <button
          className={classes.DateRangePicker__close}
          type="button"
          onClick={() => {
            setFocusedInput(null);
          }}
        />
      </div>
    );
  };

  return (
    <DateRangePicker
      startDate={value.start}
      startDateId="date-from"
      endDate={value.end}
      endDateId="date-to"
      onDatesChange={({ startDate, endDate }) => {
        const singleDay = datesAreSame(startDate, endDate);
        onChange({ start: startDate, end: endDate, singleDay });
        setTouched(true);
        temporaryEndDate = endDate;
      }}
      focusedInput={focusedInput}
      onFocusChange={(input) => {
        document.activeElement.blur(); // don't show keyboard on mobile

        if (input === START_DATE && focusedInput === END_DATE) return;
        if (!focusedInput && input !== START_DATE) {
          setFocusedInput(START_DATE);
        } else {
          setFocusedInput(input);
        }
        if (input == null && !temporaryEndDate) {
          onChange({ ...value, end: value.start });
        }
        if (fixPosition) {
          setTimeout(() => fixDatePickerLocation("DateRangePicker"), 50);
        }
      }}
      minimumNights={0}
      numberOfMonths={1}
      displayFormat="DD/MM/YYYY"
      anchorDirection={ANCHOR_LEFT}
      openDirection={direction}
      disableScroll={false}
      withPortal={withPortal}
      withFullScreenPortal={fullScreen}
      isOutsideRange={notWithinRange}
      initialVisibleMonth={() =>
        (value.start || minDate).clone().startOf("month")
      }
      minDate={minDate}
      appendToBody
      renderCalendarInfo={() => {
        return fullScreen ? null : renderCalendarFooter();
      }}
      isDayBlocked={(date) => {
        let isExcludedDate = false;
        if (
          excludedDays &&
          excludedDays.includes(moment.weekdays(date.weekday()).toLowerCase())
        ) {
          isExcludedDate = true;
        } else if (
          excludedDates &&
          excludedDates.includes(moment(date).format("DD/MM/YYYY"))
        ) {
          isExcludedDate = true;
        }
        return isExcludedDate;
      }}
    />
  );
};

VsDateRangePicker.propTypes = {
  value: PropTypes.shape({
    start: PropTypes.instanceOf(moment),
    end: PropTypes.instanceOf(moment),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  direction: PropTypes.string,
  fixPosition: PropTypes.bool,
  withPortal: PropTypes.bool,
  allowPast: PropTypes.bool,
  minDate: PropTypes.instanceOf(moment),
};

export default VsDateRangePicker;
